import cx from "classnames";
import { forwardRef, ReactNode } from "react";

import { AnalyticsCommonProps } from "hooks/useAnalytics";
import { IconComponent } from "types/Icon";

import BaseActionButton, { BaseActionButtonProps } from "../BaseAction/Button";
import Icon, { IconProps } from "../Icon";
import TextEllipsis from "../TextEllipsis";
import styles from "./styles.module.css";

export type ButtonProps = BaseActionButtonProps & {
  variant:
    | "primary"
    | "secondary"
    | "contrast"
    | "dangerPrimary"
    | "dangerSecondary"
    | "ghost"
    | "ghostInversed"
    | "ghostDanger"
    | "outlineOnDark";
  size?: "small" | "medium";
  startIcon?: IconComponent;
  startIconSize?: IconProps["size"];
  startIconRotate?: IconProps["rotate"];
  endIcon?: IconComponent;
  endIconRotate?: IconProps["rotate"];
  iconColor?: IconProps["color"];
  active?: boolean;
  disabled?: boolean;
  withTextEllipsis?: boolean;
  withRotateTransition?: IconProps["withTransition"];
  children: ReactNode;
} & AnalyticsCommonProps;

const Button = forwardRef(function Button(
  props: ButtonProps,
  ref: React.ForwardedRef<HTMLButtonElement>
) {
  const {
    size = "medium",
    variant,
    onPress,
    children,
    className,
    startIcon,
    startIconSize,
    startIconRotate,
    endIcon,
    endIconRotate,
    active,
    withTextEllipsis,
    withRotateTransition,
    iconColor,
    ...restProps
  } = props;

  return (
    <BaseActionButton
      ref={ref}
      className={cx(
        styles.button,
        styles[size],
        styles[variant],
        {
          [styles.active]: active,
          [styles.disabled]: restProps.disabled,
          [styles.textEllipsis]: withTextEllipsis,
        },
        className
      )}
      onPress={onPress}
      {...restProps}
    >
      {startIcon && (
        <Icon
          color={iconColor}
          noShrink
          src={startIcon}
          aria-hidden
          rotate={startIconRotate}
          size={startIconSize}
          withTransition={withRotateTransition}
        />
      )}
      {withTextEllipsis ? (
        <TextEllipsis tooltip={children} tooltipWidthMode="maxWidthSm" tooltipPlacement="bottom">
          {(props) => <span {...props}>{children}</span>}
        </TextEllipsis>
      ) : (
        children
      )}
      {endIcon && (
        <Icon
          color={iconColor}
          noShrink
          src={endIcon}
          aria-hidden
          rotate={endIconRotate}
          withTransition={withRotateTransition}
        />
      )}
    </BaseActionButton>
  );
});

Button.displayName = "DS.Button";

export default Button;
